import React, { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styles from "./BookedInterest.module.css";
import { COUNTRY_CODE_MAP } from "../../../constants/constant";

const BookedInterest = ({ data, loading = false }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("current"); // 'current' or 'previous'
  const history = useHistory();

  // Get month names from data and convert to readable format
  const { currentMonthName, previousMonthName } = useMemo(() => {
    if (!data) {
      return { currentMonthName: '', previousMonthName: '' };
    }

    // Convert month number format (MM-yy) to month name format (MMM-yy)
    // e.g., "11-25" -> "Nov-25", "10-25" -> "Oct-25"
    const convertToMonthName = (monthStr) => {
      if (!monthStr) return '';
      const [month, year] = monthStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(month, 10) - 1; // Convert to 0-based index
      return `${monthNames[monthIndex]}-${year}`;
    };

    const currentName = convertToMonthName(data.previousMonthName || '');  // More recent month (Nov)
    const previousName = convertToMonthName(data.previousBeforeMonthName || ''); // Older month (Oct)

    return { 
      currentMonthName: currentName, 
      previousMonthName: previousName
    };
  }, [data]);

  // Transform data for display
  const displayData = useMemo(() => {
    if (!data) return [];
    
    // Get the data for the selected period (now it's an array of CountryInterestDTO)
    const monthData = selectedPeriod === "current" ? data.previousMonth : data.previousBeforeMonth;
    if (!monthData || !Array.isArray(monthData)) return [];

    // Map the array directly - each item has country, amount, and currency
    return monthData.map((item, index) => ({
      id: index + 1,
      country: item.country,
      amount: item.amount || 0,
      currency: item.currency
    }));
  }, [data, selectedPeriod]);

  const handleToggle = (period) => {
    setSelectedPeriod(period);
  };

  const handleCountryClick = (country) => {
    const fiscalMonthKey = selectedPeriod === "current" 
      ? data?.previousMonthName 
      : data?.previousBeforeMonthName;
    let convertedFiscalMonthKey = fiscalMonthKey;
    if (fiscalMonthKey) {
      const [month, year] = fiscalMonthKey.split('-');
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      
      // Add 2 to the month to get fiscal period
      let fiscalMonth = monthNum + 2;
      let fiscalYear = 2000 + yearNum;
      
      // Handle year rollover (if month goes beyond 12, increment year)
      if (fiscalMonth > 12) {
        fiscalMonth -= 12;
        fiscalYear += 1;
      }
      
      // Format with leading zero for month
      const fiscalMonthStr = fiscalMonth.toString().padStart(2, '0');
      convertedFiscalMonthKey = `${fiscalMonthStr}-${fiscalYear}`;
    }
    
    if (convertedFiscalMonthKey && country) {
      const navigationState = {
        country: country,
        countryCode: country,
        countryName: COUNTRY_CODE_MAP[country] || country,
        fiscalMonthKey: convertedFiscalMonthKey,
        activeTab: 'logs'  // Redirect to Logs tab instead of BP check
      };
      
      // Navigate to calculations page with country and fiscal month key
      history.push({
        pathname: '/interestBearing/calculationsPage',
        state: navigationState
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>Booked Interest</h3>
        </div>
        {(currentMonthName || previousMonthName) && (
          <div className={styles.toggleContainer}>
            {previousMonthName && (
              <button
                className={`${styles.toggleButton} ${selectedPeriod === "previous" ? styles.active : ""}`}
                onClick={() => handleToggle("previous")}
              >
                {previousMonthName}
              </button>
            )}
            {currentMonthName && (
              <button
                className={`${styles.toggleButton} ${selectedPeriod === "current" ? styles.active : ""}`}
                onClick={() => handleToggle("current")}
              >
                {currentMonthName}
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loadingState}>Loading...</div>
        ) : displayData.length === 0 ? (
          <div className={styles.emptyState}>No booked interest data</div>
        ) : (
          <table className={styles.table}>
            <tbody>
              {displayData.map((item) => (
                <tr 
                  key={item.id} 
                  className={styles.tableRow}
                  onClick={() => handleCountryClick(item.country)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={styles.countryCell}>
                    {COUNTRY_CODE_MAP[item.country] || item.country}
                  </td>
                  <td className={styles.amountCell}>
                    {Number(item.amount).toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })} {item.currency && <span className={styles.currency}>{item.currency}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BookedInterest;
